import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import SqrtInput from "../../../../components/inputs/SqrtInput";

// ==== FUNKCJE POMOCNICZE ====

function evaluateExpression(expr) {
  const cleaned = String(expr ?? "")
    .replace(/\s/g, "")
    .replace(/\u200b/g, "")
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
    .replace(/\\\\sqrt\{([\d.,]+)\}/g, "√$1")
    .replace(/\\sqrt\{([\d.,]+)\}/g, "√$1")
    .replace(/sqrt\{([\d.,]+)\}/g, "√$1")
    .replace(/sqrt/g, "√")
    .replace(/√\{([^}]+)\}/g, "√$1")
    .replace(/√\(([^)]+)\)/g, "√$1")
    .replace(/,/g, ".");

  try {
    const parsed = cleaned
      .replace(/(\d*)√(\d+(\.\d+)?)/g, (_, a, b) => `${a || "1"}*Math.sqrt(${b})`)
      .replace(/√(\d+(\.\d+)?)/g, (_, b) => `Math.sqrt(${b})`);

    return Function(`return ${parsed}`)();
  } catch {
    return NaN;
  }
}

function normalizeCanonical(str) {
  return String(str ?? "")
    .replace(/\s/g, "")
    .replace(/\u200b/g, "")
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
    .replace(/\\√/g, "√")
    .replace(/\\\\sqrt/g, "√")
    .replace(/\\sqrt/g, "√")
    .replace(/sqrt/g, "√")
    .replace(/√\{([^}]+)\}/g, "√$1")
    .replace(/√\(([^)]+)\)/g, "√$1")
    .replace(/^1√/, "√")
    .replace(/√0*(\d+)/g, (_, d) => `√${parseInt(d, 10)}`)
    .replace(/^0*(\d+)$/, (_, d) => `${parseInt(d, 10)}`)
    .trim();
}

function simplifySqrt(n) {
  let factor = 1;
  for (let i = Math.floor(Math.sqrt(n)); i >= 2; i--) {
    if (n % (i * i) === 0) {
      factor = i;
      break;
    }
  }
  const outside = factor;
  const inside = n / (factor * factor);

  if (inside === 1) return `${outside}`;
  if (outside === 1) return `\\sqrt{${inside}}`;
  return `${outside}\\sqrt{${inside}}`;
}

function losuj() {
  return Math.floor(Math.random() * 17) - 8;
}

function areCollinear(A, B, C) {
  return (B.x - A.x) * (C.y - A.y) === (B.y - A.y) * (C.x - A.x);
}

function coerceToString(val) {
  if (val == null) return "";
  if (typeof val === "string") return val;
  if (typeof val === "number" || typeof val === "boolean") return String(val);
  if (typeof val === "object" && val?.target && "value" in val.target) {
    return String(val.target.value ?? "");
  }
  if (typeof val === "object" && "raw" in val) return String(val.raw ?? "");
  if (typeof val === "object" && "latex" in val) return String(val.latex ?? "");
  if (typeof val === "object" && "text" in val) return String(val.text ?? "");
  if (typeof val === "object" && "expr" in val) return String(val.expr ?? "");
  return "";
}

// ==== GENERATOR ====

export function generateTask() {
  const A = { x: losuj(), y: losuj() };
  let B, C;

  do {
    B = { x: losuj(), y: losuj() };
  } while (B.x === A.x && B.y === A.y);

  do {
    C = { x: losuj(), y: losuj() };
  } while (
    (C.x === A.x && C.y === A.y) ||
    (C.x === B.x && C.y === B.y) ||
    areCollinear(A, B, C)
  );

  const dist = (P, Q) => (Q.x - P.x) ** 2 + (Q.y - P.y) ** 2;
  const a = dist(B, C);
  const b = dist(A, C);
  const c = dist(A, B);

  const parts = [a, b, c].map(simplifySqrt);

  const constants = parts.filter((x) => !x.includes("\\sqrt")).map(Number);
  const roots = parts.filter((x) => x.includes("\\sqrt"));
  const constantSum = constants.reduce((acc, val) => acc + val, 0);

  const rootGroups = {};
  for (const term of roots) {
    const match = term.match(/^(\d*)\\sqrt\{(\d+)\}$/);
    if (!match) continue;
    const [, coeffRaw, radicand] = match;
    const coeff = coeffRaw === "" ? 1 : parseInt(coeffRaw, 10);
    rootGroups[radicand] = (rootGroups[radicand] || 0) + coeff;
  }

  const reducedRoots = Object.entries(rootGroups).map(([r, coeff]) =>
    coeff === 1 ? `\\sqrt{${r}}` : `${coeff}\\sqrt{${r}}`
  );

  const finalParts = [...(constantSum ? [constantSum] : []), ...reducedRoots];
  const final = finalParts.join(" + ");

  const originalPer = parts.join(" + ");

  return {
    type: "PerimeterTask",
    data: { A, B, C, per: final, originalPer, a, b, c },
  };
}

// ==== RENDERING ====

export function renderTask({ data }) {
  const { A, B, C } = data;
  return (
    <>
      <p>
        Oblicz obwód trójkąta <InlineMath math="ABC" />, którego wierzchołkami są punkty:
      </p>
      <p>
        <InlineMath math={`A=(${A.x},${A.y})`} />,{" "}
        <InlineMath math={`B=(${B.x},${B.y})`} />,{" "}
        <InlineMath math={`C=(${C.x},${C.y})`} />
      </p>
    </>
  );
}

export function renderInput(_, userAnswer, onChange, disabled) {
  return <SqrtInput value={coerceToString(userAnswer)} onChange={onChange} disabled={disabled} wide={true} />;
}

// ==== CHECK ANSWER ====

function hasDuplicateRadicals(expr) {
  const terms = expr.split("+").map((t) => t.trim());
  const roots = {};

  for (const term of terms) {
    const match = term.match(/^([0-9.-]*)√([0-9.]+)$/);
    if (match) {
      const radicand = match[2];
      roots[radicand] = (roots[radicand] || 0) + 1;
    }
  }
  return Object.values(roots).some((count) => count > 1);
}

function reduceRadicals(terms) {
  const grouped = {};

  for (let term of terms) {
    const match = term.match(/^([0-9.-]*)√([0-9.]+)$/);
    if (match) {
      const coeff = parseFloat(match[1] || "1");
      const root = match[2];
      grouped[`√${root}`] = (grouped[`√${root}`] || 0) + coeff;
    } else if (!isNaN(Number(term))) {
      grouped["const"] = (grouped["const"] || 0) + parseFloat(term);
    } else {
      grouped[term] = (grouped[term] || 0) + 1;
    }
  }

  const result = [];

  if (grouped["const"]) result.push(grouped["const"].toString());

  for (const [key, value] of Object.entries(grouped)) {
    if (key === "const") continue;
    if (value === 1) result.push(`${key}`);
    else result.push(`${value}${key}`);
  }

  return result.join(" + ");
}

export function checkAnswer({ data }, userAnswer) {
  const ua = coerceToString(userAnswer);
  if (!ua) return false;

  const expectedLatex = String(data.per);
  const expectedNorm = normalizeCanonical(expectedLatex);
  const givenNorm = normalizeCanonical(ua);

  const expectedVal = evaluateExpression(expectedNorm);
  const givenVal = evaluateExpression(givenNorm);

  const isClose =
    Number.isFinite(expectedVal) &&
    Number.isFinite(givenVal) &&
    Math.abs(expectedVal - givenVal) < 0.01;

  if (!isClose) return false;

  // 🔁 Redukcja i sortowanie składników (na "√" po normalizeCanonical)
  const sortReduced = (expr) =>
    reduceRadicals(expr.split("+").map((x) => x.trim()))
      .split("+")
      .map((s) => s.trim())
      .sort()
      .join("+");

  const sortedExpected = sortReduced(expectedNorm);
  const sortedGiven = sortReduced(givenNorm);

  const hasDuplicates = hasDuplicateRadicals(givenNorm);

  // jeśli forma już OK i nie ma duplikatów pierwiastków tego samego typu -> pełna poprawność
  if (sortedExpected === sortedGiven && !hasDuplicates) {
    return true;
  }

  // 🔔 wartość dobra, ale forma nie – pokaż konkretnie: przed = po
  const beforeLatex = String(data.originalPer || "");
  const afterLatex = String(data.per || "");

  return {
    correct: true,
    needsSimplification: true,
    expected: String(data.per || ""),
    messageType: "warning",
    message: `Wynik jest dobry, ale można go uprościć: $${beforeLatex} = ${afterLatex}$.`,
  };
}

// ==== ANSWER / EXPLANATION ====

export function renderAnswer({ data }) {
  return <InlineMath math={data.per} />;
}

export function renderExplanation({ data }) {
  const { A, B, C, a, b, c } = data;

  const fs = (n) => (n < 0 ? `(${n})` : `${n}`);
  const fsPow = (n) => (n < 0 ? `(${n})^2` : `${n}^2`);

  const dxAB = B.x - A.x;
  const dyAB = B.y - A.y;
  const dxAC = C.x - A.x;
  const dyAC = C.y - A.y;
  const dxBC = C.x - B.x;
  const dyBC = C.y - B.y;

  const abSqrt = simplifySqrt(c);
  const acSqrt = simplifySqrt(b);
  const bcSqrt = simplifySqrt(a);

  return (
    <div className="space-y-2">
      <p>
        <strong>Krok 1:</strong> Korzystamy ze wzoru na długość każdego boku trójkąta:
      </p>
      <BlockMath math="|AB| = \sqrt{(x_B - x_A)^2 + (y_B - y_A)^2}" />
      <BlockMath math="|AC| = \sqrt{(x_C - x_A)^2 + (y_C - y_A)^2}" />
      <BlockMath math="|BC| = \sqrt{(x_C - x_B)^2 + (y_C - y_B)^2}" />

      <p>
        <strong>Krok 2:</strong> Podstawiamy współrzędne i obliczamy:
      </p>

      <BlockMath
        math={
          abSqrt === `\\sqrt{${c}}`
            ? `|AB| = \\sqrt{${fsPow(dxAB)} + ${fsPow(dyAB)}} = \\sqrt{${dxAB ** 2}+${dyAB ** 2}} = \\sqrt{${c}}`
            : `|AB| = \\sqrt{${fsPow(dxAB)} + ${fsPow(dyAB)}} = \\sqrt{${dxAB ** 2}+${dyAB ** 2}} = \\sqrt{${c}} = ${abSqrt}`
        }
      />

      <BlockMath
        math={
          acSqrt === `\\sqrt{${b}}`
            ? `|AC| = \\sqrt{${fsPow(dxAC)} + ${fsPow(dyAC)}} = \\sqrt{${dxAC ** 2}+${dyAC ** 2}} = \\sqrt{${b}}`
            : `|AC| = \\sqrt{${fsPow(dxAC)} + ${fsPow(dyAC)}} = \\sqrt{${dxAC ** 2}+${dyAC ** 2}} = \\sqrt{${b}} = ${acSqrt}`
        }
      />

      <BlockMath
        math={
          bcSqrt === `\\sqrt{${a}}`
            ? `|BC| = \\sqrt{${fsPow(dxBC)} + ${fsPow(dyBC)}} = \\sqrt{${dxBC ** 2}+${dyBC ** 2}} = \\sqrt{${a}}`
            : `|BC| = \\sqrt{${fsPow(dxBC)} + ${fsPow(dyBC)}} = \\sqrt{${dxBC ** 2}+${dyBC ** 2}} = \\sqrt{${a}} = ${bcSqrt}`
        }
      />

      <p>
        <strong>Krok 3:</strong> Sumujemy długości boków i upraszczamy jeśli się da:
      </p>

      <BlockMath math={`|AB| + |BC| + |AC| = ${abSqrt} + ${bcSqrt} + ${acSqrt}`} />

      {(() => {
        const parts = [abSqrt, bcSqrt, acSqrt];

        const constants = parts.filter((x) => !x.includes("\\sqrt")).map((x) => Number(x));
        const roots = parts.filter((x) => x.includes("\\sqrt"));
        const constantSum = constants.reduce((acc, val) => acc + val, 0);

        const rootGroups = {};
        for (const term of roots) {
          const match = term.match(/^(\d*)\\sqrt\{(\d+)\}$/);
          if (!match) continue;
          const [, coeffRaw, radicand] = match;
          const coeff = coeffRaw === "" ? 1 : parseInt(coeffRaw, 10);
          rootGroups[radicand] = (rootGroups[radicand] || 0) + coeff;
        }

        const reducedRoots = Object.entries(rootGroups).map(([r, coeff]) =>
          coeff === 1 ? `\\sqrt{${r}}` : `${coeff}\\sqrt{${r}}`
        );

        const finalParts = [...(constantSum ? [constantSum] : []), ...reducedRoots];
        const final = finalParts.join(" + ");

        const original = parts.join(" + ");
        if (final === original) return null;

        return <BlockMath math={`= ${final}`} />;
      })()}
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  const s = coerceToString(userAnswer);
  if (s.trim() === "") return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={s} />;
}
